/* eslint-disable */
import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { Button, FormikInput } from "../../../ui";
import { addSubAdminInitialValues } from "../../../../utils/formik/initialValues";
import {
  addSubAdminValidationSchema,
  editSubAdminValidationSchema,
} from "../../../../utils/formik/validationSchema";
import styles from "../addNewLocationForm/style.module.scss";
import jwtInterceoptor from "../../../../api/jwtInterceptor";
import { GET_API_URLS } from "../../../../utils/constant";

const AddSubAdminForm = (props) => {
  const [allModules, setAllModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialValues, setInitialValues] = useState(addSubAdminInitialValues);

  // Fetch modules from API
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await jwtInterceoptor.get(GET_API_URLS.MODULE);
        setAllModules(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching modules:", error);
        setLoading(false);
      }
    };

    fetchModules();
  }, []);

  // Convert initial values when modules are loaded and form data is available
  useEffect(() => {
    if (props.formInitialValues) {
      console.log("Form initial values received:", props.formInitialValues);
      // For edit mode, if user already has modules structure, use it immediately
      if (props.formInitialValues.modules && Array.isArray(props.formInitialValues.modules)) {
        const convertedValues = getInitialValues();
        console.log("Edit mode: Setting initial values with existing modules:", convertedValues);
        setInitialValues(convertedValues);
      } else if (allModules.length > 0) {
        // For old structure conversion, wait for modules to load
        const convertedValues = getInitialValues();
        console.log("Edit mode: Converting and setting initial values:", convertedValues);
        setInitialValues(convertedValues);
      }
    } else {
      // Create mode - use default initial values
      console.log("Create mode: Using default initial values");
      setInitialValues(addSubAdminInitialValues);
    }
  }, [allModules, props.formInitialValues]);

  // Debug: Track initial values changes
  useEffect(() => {
    console.log("Initial values state changed:", initialValues);
  }, [initialValues]);

  // Helper function to handle project checkbox change
  const handleProjectChange = (projectId, isChecked, setFieldValue, values) => {
    const project = allModules.find(p => p.id === projectId);
    if (!project) return;

    let updatedModules = [...(values.modules || [])];
    
    console.log(`Project ${projectId} checkbox changed to ${isChecked}`);
    console.log("Current modules:", updatedModules);
    
    if (isChecked) {
      // Add all modules from this project (except module 8)
      const projectModules = project.modules
        .filter(module => module.id !== 8)
        .map(module => module.id);
      
      // Add modules that aren't already selected
      projectModules.forEach(moduleId => {
        if (!updatedModules.includes(moduleId)) {
          updatedModules.push(moduleId);
        }
      });
    } else {
      // Remove all modules from this project
      const projectModules = project.modules.map(module => module.id);
      updatedModules = updatedModules.filter(moduleId => !projectModules.includes(moduleId));
    }
    
    console.log("Updated modules after project change:", updatedModules);
    setFieldValue('modules', updatedModules);
  };

  // Helper function to handle module checkbox change
  const handleModuleChange = (projectId, moduleId, isChecked, setFieldValue, values) => {
    let updatedModules = [...(values.modules || [])];
    
    console.log(`Module ${moduleId} checkbox changed to ${isChecked}`);
    console.log("Current modules:", updatedModules);
    
    if (isChecked) {
      // Add module if not already selected
      if (!updatedModules.includes(moduleId)) {
        updatedModules.push(moduleId);
      }
    } else {
      // Remove module
      updatedModules = updatedModules.filter(id => id !== moduleId);
    }
    
    console.log("Updated modules after module change:", updatedModules);
    setFieldValue('modules', updatedModules);
  };

  // Check if project is selected (ANY modules selected means project is selected)
  const isProjectSelected = (projectId, selectedModules) => {
    const project = allModules.find(p => p.id === projectId);
    if (!project) return false;
    
    // Project is considered "selected" if any of its modules are selected
    const isSelected = project.modules.some(module => 
      selectedModules?.includes(module.id)
    );
    
    console.log(`Project ${projectId} selection state:`, isSelected, "Modules:", selectedModules);
    return isSelected;
  };

  // Check if module is selected
  const isModuleSelected = (projectId, moduleId, selectedModules) => {
    const isSelected = selectedModules?.includes(moduleId) || false;
    console.log(`Module ${moduleId} selection state:`, isSelected, "Selected modules:", selectedModules);
    return isSelected;
  };

  // Transform initial values for edit mode
  const getInitialValues = () => {
    if (!props.formInitialValues) {
      return addSubAdminInitialValues;
    }

    // If editing, transform the existing data to match new structure
    const initialValues = { ...props.formInitialValues };
    
    console.log("Converting initial values:", initialValues);
    
    // Convert existing user data to new modules array structure
    if (initialValues.modules && Array.isArray(initialValues.modules) && initialValues.modules.length > 0) {
      // User data already has the new modules structure - just ensure it's properly formatted
      console.log("Edit mode: User already has modules structure", initialValues.modules);
      
      // Ensure modules array contains only IDs
      const cleanedModules = initialValues.modules.map(module => 
        typeof module === 'object' ? module.id : module
      ).filter(id => id !== undefined);
      
      console.log("Cleaned modules:", cleanedModules);
      
      return {
        ...initialValues,
        modules: cleanedModules
      };
    } else if (initialValues.projects && Array.isArray(initialValues.projects)) {
      // Convert old projects structure to new modules array
      const userModules = [];
      
      console.log("Edit mode: Converting projects to modules array", initialValues.projects);
      
      initialValues.projects.forEach(project => {
        if (project.modules && Array.isArray(project.modules)) {
          project.modules.forEach(module => {
            const moduleId = typeof module === 'object' ? module.id : module;
            if (moduleId && !userModules.includes(moduleId)) {
              userModules.push(moduleId);
            }
          });
        }
      });
      
      console.log("Edit mode: Converted to modules array", userModules);
      
      return {
        ...initialValues,
        modules: userModules
      };
    }

    // Default case - ensure modules array exists
    console.log("Edit mode: Default case, creating empty modules array");
    return {
      ...initialValues,
      modules: []
    };
  };

  console.log("Edit mode - formInitialValues:", props.formInitialValues);
  console.log("Converted initialValues:", initialValues);
  console.log("All modules loaded:", allModules);
  
  if (loading) {
    return <div>Loading modules...</div>;
  }

  return (
    <Formik
      key={JSON.stringify(initialValues)} // Force reinitialization when initial values change
      initialValues={initialValues}
      validationSchema={
        props.formInitialValues
          ? editSubAdminValidationSchema
          : addSubAdminValidationSchema
      }
      enableReinitialize={true}
      onSubmit={(values) => {
        console.log("Form submitted with values:", values);
        console.log("Current initialValues state:", initialValues);
        
        // Transform modules structure to backend format
        const transformedValues = { ...values };
        
        // Ensure modules array contains only IDs
        if (values.modules && values.modules.length > 0) {
          transformedValues.modules = values.modules.map(module => 
            typeof module === 'object' ? module.id : module
          ).filter(id => id !== undefined);
        } else {
          transformedValues.modules = [];
        }

        // Remove projects key as we're now using modules
        delete transformedValues.projects;

        console.log("Transformed form data:", transformedValues);

        if (props.formInitialValues) {
          // For edit mode, we need to handle the data comparison more carefully
          const updatedDataObj = {};
          const keys = Object.keys(transformedValues);
          
          console.log("Edit mode - comparing data:");
          console.log("Original formInitialValues:", props.formInitialValues);
          console.log("New transformedValues:", transformedValues);
          
          keys.forEach((key) => {
            if (key === 'modules') {
              // Always include modules data for updates - this is the key fix
              updatedDataObj[key] = transformedValues[key];
              console.log("Including modules data:", transformedValues[key]);
            } else if (key !== 'id') {
              // For other fields, compare with original values
              const originalValue = props.formInitialValues[key];
              const newValue = transformedValues[key];
              
              console.log(`Comparing ${key}:`, originalValue, "vs", newValue);
              
              if (JSON.stringify(originalValue) !== JSON.stringify(newValue)) {
                updatedDataObj[key] = newValue;
                console.log(`Field ${key} changed, including in update`);
              } else {
                console.log(`Field ${key} unchanged, skipping`);
              }
            }
          });
          
          // Always include the ID for updates
          updatedDataObj.id = transformedValues.id;
          
          // Ensure modules is always included in updates
          if (!updatedDataObj.hasOwnProperty('modules')) {
            updatedDataObj.modules = transformedValues.modules || [];
            console.log("Added missing modules data to update:", updatedDataObj.modules);
          }
          
          console.log("Final updated data object:", updatedDataObj);
          props.updateData(updatedDataObj);
        } else {
          props.data(transformedValues);
        }
      }}
    >
      {({ errors, touched, getFieldProps, values, setFieldValue }) => (
        <Form className={styles.addLocationForm}>
          <section>
            <div>
              <FormikInput
                type="text"
                name="first_name"
                label="First Name"
                formikProps={getFieldProps("first_name")}
                formikTouch={touched.first_name}
                formikErrors={errors.first_name}
                style={
                  touched.first_name && errors.first_name
                    ? { borderColor: "red" }
                    : null
                }
              />
              <FormikInput
                type="text"
                name="last_name"
                label="Last Name"
                formikProps={getFieldProps("last_name")}
                formikTouch={touched.last_name}
                formikErrors={errors.last_name}
                style={
                  touched.last_name && errors.last_name
                    ? { borderColor: "red" }
                    : null
                }
              />
            </div>
            <div>
              <FormikInput
                type="email"
                name="email"
                label="Email"
                formikProps={getFieldProps("email")}
                formikTouch={touched.email}
                formikErrors={errors.email}
                style={
                  touched.email && errors.email ? { borderColor: "red" } : null
                }
              />
              {/* <FormikInput
                type="text"
                name="address"
                label="Location"
                formikProps={getFieldProps("address")}
                formikTouch={touched.address}
                formikErrors={errors.address}
                style={
                  touched.address && errors.address
                    ? { borderColor: "red" }
                    : null
                }
              /> */}
            </div>
            <div>
              <FormikInput
                type="text"
                name="email"
                label="Username"
                formikProps={getFieldProps("email")}
                formikTouch={touched.email}
                formikErrors={errors.email}
                disabled={true}
                style={
                  touched.email && errors.email ? { borderColor: "red" } : null
                }
              />
              <FormikInput
                type="password"
                name="password"
                label="Password"
                formikProps={getFieldProps("password")}
                formikTouch={touched.password}
                formikErrors={errors.password}
                style={
                  touched.password && errors.password
                    ? { borderColor: "red" }
                    : null
                }
              />
            </div>
            <div className={styles.amenitiesContainer}>
              <h3 id="checkbox-group">Module Access</h3>
              <div role="group" aria-labelledby="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                {allModules.map((project) => (
                  <div key={project.id} style={{ 
                    width: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '15px',
                    backgroundColor: '#fafafa'
                  }}>
                    {/* Project Level Checkbox */}
                    <label className="material-checkbox" style={{ 
                      fontWeight: 'bold', 
                      fontSize: '16px',
                      display: 'block',
                      marginBottom: '15px',
                      color: '#333'
                    }}>
                      <input
                        type="checkbox"
                        checked={isProjectSelected(project.id, values.modules)}
                        onChange={(e) => handleProjectChange(project.id, e.target.checked, setFieldValue, values)}
                      />
                      <span className="checkmark"></span>
                      {project.name}
                    </label>
                    
                    {/* Module Level Checkboxes */}
                    <div style={{ 
                      marginLeft: '25px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '15px 20px',
                      paddingTop: '10px',
                      borderTop: '1px solid #e0e0e0'
                    }}>
                      {project.modules
                        .filter(module => module.id !== 8) // Exclude module 8
                        .map((module) => (
                          <label key={module.id} className="material-checkbox" style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '14px',
                            color: '#555',
                            minWidth: '180px',
                            flex: '0 0 calc(20% - 16px)', // 5 items per row with gap
                            whiteSpace: 'nowrap'
                          }}>
                            <input
                              type="checkbox"
                              checked={isModuleSelected(project.id, module.id, values.modules)}
                              onChange={(e) => handleModuleChange(project.id, module.id, e.target.checked, setFieldValue, values)}
                            />
                            <span className="checkmark"></span>
                            {module.name}
                          </label>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Button
                type="submit"
                label={props.loading ? "Saving..." : "Save"}
                disabled={props.loading || !values.modules || values.modules.length === 0}
                primary
                style={{ maxWidth: "20rem", margin: "0 0 0 auto" }}
              />
            </div>
          </section>
        </Form>
      )}
    </Formik>
  );
};

export default AddSubAdminForm;
